/* eslint-disable no-underscore-dangle */
/* eslint-disable max-len */
import { sentenceCase } from 'sentence-case';
import { colors } from '../../../../../packages';
import { ReportFilterRule } from '../../../../../redux/modules/reporting/types';
import { REPORT_FILTERS } from '../../../../../constants/CommonConstants';
import { getLocalDateAsUTC, getTimeDifference } from '../../../../../utils/dayjs';
import { toDateString } from '../../../../../utils/CommonUtils';
import {
  getReportAllFiltersSubQuery,
  getReportAllFiltersSubQueryReferralPartner,
  getReportFilterSubQuery,
  getReportInternalFiltersSubQuery,
  REPORT_CONSTANTS,
  REPORT_SECTION_NAMES,
} from '../../ReportDetail.constants';

const boxDataMap = {
  'total chatbots': 'totalChatbots',
  'total assignments count': 'averageAssignments',
  'last assigned': 'lastAssigned',
};

export const getBoxData = (chatbotReport, report) => {
  const boxData = [
    // TODO: replace with total chatbots count when available
    // { label: 'total chatbots', value: chatbotReport?.total_chatbots_count ?? 0 },
    { label: 'total chatbots', value: chatbotReport?.aggregations?.total_chatbots?.value ?? 0 },
    {
      label: 'total assignments count',
      value: chatbotReport ? chatbotReport.aggregations.total_assignments.value : 0,
    },
    {
      label: 'last assigned',
      value: chatbotReport?.aggregations.last_assigned_date.hits.hits[0]
        ? getTimeDifference(
            new Date(chatbotReport.aggregations.last_assigned_date.hits.hits[0]._source.assignedAt) ??
              getLocalDateAsUTC(),
            getLocalDateAsUTC(),
          )
        : '',
    },
  ];

  return boxData.filter(item => report?.data?.chatbotSection[boxDataMap[item.label]]?.isChecked) ?? [];
};

export const getLast30DaysAssigned = report =>
  report
    ? report.aggregations.last_30_days_assigned.counts.buckets.map(bucket => bucket.doc_count).slice(0, 30)
    : [];

export const getLast30DaysCompleted = report =>
  report
    ? report.aggregations.last_30_days_completed.counts.buckets.map(bucket => bucket.doc_count).slice(0, 30)
    : [];

export const getByNumberOfMembersData = report =>
  report
    ? report.aggregations.by_most_members.by_members.buckets.map(bucket => ({
        name: bucket.key,
        total: bucket.doc_count,
        lastUpdated: toDateString(bucket.last_assigned_date.hits.hits[0]._source.lastModifiedDate),
      }))
    : [];

export const getByCompletionAttemptsData = report =>
  report
    ? report.aggregations.by_most_completed.by_members.buckets.map(bucket => ({
        name: bucket.key,
        total: bucket.doc_count,
        lastUpdated: toDateString(bucket.last_assigned_date.hits.hits[0]._source.lastModifiedDate),
      }))
    : [];

export const memberColumns = [
  {
    id: 'name',
    label: 'Chatbot',
    sortable: false,
  },
  {
    id: 'total',
    label: 'Assignments',
    sortable: false,
    textAlign: 'center',
    className: 'color-secondary-500',
  },
  {
    id: 'lastUpdated',
    label: 'Last',
    sortable: false,
  },
  {
    id: 'actions',
    label: '',
    sortable: false,
  },
];

export const attempColumns = [
  {
    id: 'name',
    label: 'Chatbot',
    sortable: false,
  },
  {
    id: 'total',
    label: 'Attempts',
    sortable: false,
    textAlign: 'center',
    className: 'color-secondary-500',
  },
  {
    id: 'lastUpdated',
    label: 'Last',
    sortable: false,
  },
  {
    id: 'actions',
    label: '',
    sortable: false,
  },
];

const chartColors = [
  colors.success500,
  colors.primary500,
  colors.neutral300,
  colors.orange400,
  colors.secondary500,
  colors.cyan500,
  colors.indigo500,
  colors.purple500,
  colors.rose500,
  colors.destructive500,
];
export const getChartSeries = report =>
  report
    ? report.aggregations.chatbots_list.buckets.map(bucket => ({
        title: bucket.key,
        subtitle: `Last assigned: ${
          bucket.last_assigned_date.hits.hits[0]._source.assignedAt
            ? toDateString(bucket.last_assigned_date.hits.hits[0]._source.assignedAt)
            : ''
        }`,
        chartData: bucket.by_status.buckets.map((item, i) => ({
          name: sentenceCase(item.key || ''),
          color: chartColors[i],
          data: [item.doc_count],
        })),
        donutOptTitle: 'Assignments',
        donutOptTotal: bucket.doc_count,
      }))
    : [];

export const getChatbotReportQuery = report => {
  let allFilters = JSON.parse(JSON.stringify(report.reportFilters));
  if (report?.reportFilters?.length || report?.referralPartnerId) {
    const allInsuranceFilters = report.reportFilters?.filter(
      filter => filter.type === REPORT_FILTERS.INSURANCE,
    );
    if (allInsuranceFilters?.length) {
      allFilters = [
        ...report.reportFilters?.filter(filter => filter.type !== REPORT_FILTERS.INSURANCE),
        {
          type: REPORT_FILTERS.INSURANCE,
          insurance: {
            carriers: allInsuranceFilters?.map(filter => filter.insurance?.carriers)?.flat(),
            state: '',
          },
          value: '',
          profileElement: '',
          states: [],
          levelOfEngagements: [],
          providers: [],
          rule: ReportFilterRule.EQUALS,
        },
      ];
    }
    return {
      size: 0,
      query: {
        bool: {
          ...(report?.data?.chatbotSection?.chatbotIds?.conversationIds?.length
            ? {
                filter: [
                  {
                    terms: {
                      'conversation_id.keyword': report.data.chatbotSection.chatbotIds.conversationIds,
                    },
                  },
                ],
              }
            : {}),
          must: [
            ...allFilters?.map(filter => filter.profileElement && getReportFilterSubQuery(filter)),
            ...allFilters?.map(filter => getReportAllFiltersSubQuery(filter)),
            ...getReportAllFiltersSubQueryReferralPartner(report?.referralPartnerId),
            ...getReportInternalFiltersSubQuery(report, REPORT_SECTION_NAMES.chatbotSection),
          ]?.filter(obj => Object.keys(obj)?.length > 0),
        },
      },
      aggs: {
        total_members: {
          cardinality: {
            field: 'member_id.keyword',
          },
        },
        total_chatbots: {
          cardinality: {
            field: 'chatbot_name.keyword',
          },
        },
        total_assignments: {
          cardinality: {
            field: 'mongo_id.keyword',
            precision_threshold: 22000,
          },
        },
        last_assigned_date: {
          top_hits: {
            _source: 'assignedAt',
            sort: [{ 'assignedAt.keyword': { order: 'desc' } }],
            size: 1,
          },
        },
        last_30_days_assigned: {
          filter: {
            bool: {
              must: [
                {
                  exists: {
                    field: 'chatbot_id',
                  },
                },
                {
                  range: {
                    lastModifiedDate: {
                      gte: 'now-30d/d',
                      lte: 'now',
                    },
                  },
                },
              ],
            },
          },
          aggs: {
            counts: {
              date_histogram: {
                field: 'lastModifiedDate',
                calendar_interval: '1d',
              },
            },
          },
        },
        last_30_days_completed: {
          filter: {
            bool: {
              must: [
                {
                  term: {
                    'chatbot_status.keyword': {
                      value: 'COMPLETED',
                    },
                  },
                },
                {
                  range: {
                    lastModifiedDate: {
                      gte: 'now-30d/d',
                      lte: 'now',
                    },
                  },
                },
              ],
            },
          },
          aggs: {
            counts: {
              date_histogram: {
                field: 'lastModifiedDate',
                calendar_interval: '1d',
              },
            },
          },
        },
        by_most_completed: {
          filter: {
            term: {
              'chatbot_status.keyword': 'COMPLETED',
            },
          },
          aggs: {
            by_members: {
              terms: {
                field: 'chatbot_name.keyword',
                size: REPORT_CONSTANTS.MAX_RECORDS_IN_QUERY,
              },
              aggs: {
                last_assigned_date: {
                  top_hits: {
                    _source: 'lastModifiedDate',
                    sort: [{ lastModifiedDate: { order: 'desc' } }],
                    size: 1,
                  },
                },
              },
            },
          },
        },
        by_most_members: {
          filter: {
            exists: {
              field: 'member_id',
            },
          },
          aggs: {
            by_members: {
              terms: {
                field: 'chatbot_name.keyword',
                size: REPORT_CONSTANTS.MAX_RECORDS_IN_QUERY,
              },
              aggs: {
                last_assigned_date: {
                  top_hits: {
                    _source: 'lastModifiedDate',
                    sort: [{ lastModifiedDate: { order: 'desc' } }],
                    size: 1,
                  },
                },
              },
            },
          },
        },

        chatbots_list: {
          terms: {
            field: 'chatbot_name.keyword',
            size: REPORT_CONSTANTS.MAX_RECORDS_IN_QUERY,
          },
          aggs: {
            by_status: {
              terms: {
                field: 'chatbot_status.keyword',
                size: 10,
              },
            },
            last_assigned_date: {
              top_hits: {
                _source: 'assignedAt',
                sort: [{ assignedAt: { order: 'desc' } }],
                size: 1,
              },
            },
          },
        },
      },
    };
  }
  return {
    size: 0,
    query: {
      bool: {
        ...(report?.data?.chatbotSection?.chatbotIds?.conversationIds?.length
          ? {
              filter: [
                {
                  terms: {
                    'conversation_id.keyword': report.data.chatbotSection.chatbotIds.conversationIds,
                  },
                },
              ],
            }
          : {}),
        must: [],
      },
    },
    aggs: {
      total_members: {
        cardinality: {
          field: 'member_id.keyword',
        },
      },
      total_chatbots: {
        cardinality: {
          field: 'chatbot_name.keyword',
        },
      },
      total_assignments: {
        cardinality: {
          field: 'mongo_id.keyword',
          precision_threshold: 22000,
        },
      },
      last_assigned_date: {
        top_hits: {
          _source: 'assignedAt',
          sort: [{ 'assignedAt.keyword': { order: 'desc' } }],
          size: 1,
        },
      },
      last_30_days_assigned: {
        filter: {
          bool: {
            must: [
              {
                exists: {
                  field: 'chatbot_id',
                },
              },
              {
                range: {
                  lastModifiedDate: {
                    gte: 'now-30d/d',
                    lte: 'now',
                  },
                },
              },
            ],
          },
        },
        aggs: {
          counts: {
            date_histogram: {
              field: 'lastModifiedDate',
              calendar_interval: '1d',
            },
          },
        },
      },
      last_30_days_completed: {
        filter: {
          bool: {
            must: [
              {
                term: {
                  'chatbot_status.keyword': {
                    value: 'COMPLETED',
                  },
                },
              },
              {
                range: {
                  lastModifiedDate: {
                    gte: 'now-30d/d',
                    lte: 'now',
                  },
                },
              },
            ],
          },
        },
        aggs: {
          counts: {
            date_histogram: {
              field: 'lastModifiedDate',
              calendar_interval: '1d',
            },
          },
        },
      },
      by_most_completed: {
        filter: {
          term: {
            'chatbot_status.keyword': 'COMPLETED',
          },
        },
        aggs: {
          by_members: {
            terms: {
              field: 'chatbot_name.keyword',
              size: REPORT_CONSTANTS.MAX_RECORDS_IN_QUERY,
            },
            aggs: {
              last_assigned_date: {
                top_hits: {
                  _source: 'lastModifiedDate',
                  sort: [{ lastModifiedDate: { order: 'desc' } }],
                  size: 1,
                },
              },
            },
          },
        },
      },
      by_most_members: {
        filter: {
          exists: {
            field: 'member_id',
          },
        },
        aggs: {
          by_members: {
            terms: {
              field: 'chatbot_name.keyword',
              size: REPORT_CONSTANTS.MAX_RECORDS_IN_QUERY,
            },
            aggs: {
              last_assigned_date: {
                top_hits: {
                  _source: 'lastModifiedDate',
                  sort: [{ lastModifiedDate: { order: 'desc' } }],
                  size: 1,
                },
              },
            },
          },
        },
      },

      chatbots_list: {
        terms: {
          field: 'chatbot_name.keyword',
          size: REPORT_CONSTANTS.MAX_RECORDS_IN_QUERY,
        },
        aggs: {
          by_status: {
            terms: {
              field: 'chatbot_status.keyword',
              size: 10,
            },
          },
          last_assigned_date: {
            top_hits: {
              _source: 'assignedAt',
              sort: [{ assignedAt: { order: 'desc' } }],
              size: 1,
            },
          },
        },
      },
    },
  };
};
